import { Canvas3d } from "./Canvas3d.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { Mesh, MeshBasicMaterial, PerspectiveCamera, SphereGeometry, Vector3 } from "three";
import { VOSceneStorage } from "../model/DataStructure.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { GammaCorrectionShader } from "three/examples/jsm/shaders/GammaCorrectionShader.js";

export interface Control3dOptions {
    canvas: Canvas3d;
    gui: dat.GUI;
    blur: number;
    showTransform: boolean;
    target: Vector3;
}

export class Control3d {


    public onChange = e5.Signal.create();

    private _target: Vector3;
    private _orbit: OrbitControls;
    private _transform: TransformControls;
    private _blur: number = 1;
    private _camera: PerspectiveCamera;
    private _transformMesh: Mesh = null;
    private _canvas: Canvas3d;

    constructor(options: Control3dOptions) {

        this._blur = options.blur;

        const canvas = options.canvas;

        this._canvas = canvas;
        this._camera = canvas.camera;
        this._orbit = new OrbitControls(canvas.camera, canvas.renderer.domElement);

        this._orbit.maxPolarAngle = Math.PI * 0.45;
        this._orbit.minPolarAngle = Math.PI * 0.1;

        // this._orbit.maxPolarAngle = Math.PI * 0.5;

        this._orbit.update();
        this._orbit.maxDistance = 120;
        this._orbit.minDistance = 25;
        this._orbit.enablePan = false;
        this._orbit.enableDamping = false;
        this._orbit.addEventListener("end", () => this.onChange.dispatch());
        this._target = this._orbit.target;
        this._target.copy(options.target);
        canvas.onUpdate.add(() => {
            this._orbit.update();
        });



        let transform: TransformControls = null;
        if (options.showTransform) {
            transform = new TransformControls(canvas.camera, canvas.renderer.domElement);
            this._transform = transform;
            transform.addEventListener('dragging-changed',
                (event) => {
                    this._orbit.enabled = !event.value;
                });
            transform.addEventListener("mouseUp", () => {
                this.target(transformMesh.position, 0.1);
            });
            // transform.addEventListener("mouseUp", () => this.onChange.dispatch());

            const transformMesh = new Mesh(
                // new SphereGeometry(),
                // new MeshBasicMaterial({
                //     wireframe: true,
                //     color: "#F0F"
                // })

            );
            this._transformMesh = transformMesh;
            transformMesh.position.copy(options.target);
            // transformMesh.visible = false;
            canvas.scene.add(transformMesh);
            transform.attach(transformMesh);

            // canvas.scene.add(transform);
            // transform.detach();

            canvas.renderer.domElement.addEventListener("click", (e) => {
                if (!e.ctrlKey) {
                    return;
                }

                let globaleScene = canvas.scene.getObjectByName("global-scene");
                if (!globaleScene) {
                    return;
                }
                let hits = canvas.hitTest(e.clientX, e.clientY, [globaleScene], true);
                if (hits.length > 0) {
                    let hit = hits[0];
                    transformMesh.position.copy(hit.point);
                    this.target(transformMesh.position, 0.1);
                }
            });
        }


        let getCamDist = () => {

            // return 1;

            let dist = this._target.distanceTo(canvas.camera.position);
            dist = e5.Calc.clamp(dist, this._orbit.minDistance, this._orbit.maxDistance);
            let relation = (dist - this._orbit.minDistance) / (this._orbit.maxDistance - this._orbit.minDistance);
            relation = e5.Calc.clamp(relation, 0, 1);
            relation = 1 - relation;
            relation *= relation;
            return relation;
        }

        const effectController = {
            focus: 50.0,
            aperture: 15,//10,
            maxblur: 0.003
        };
        var bokehPass = new BokehPass(canvas.scene, canvas.camera, effectController);
        // bokehPass.renderTargetColor = 
        // bokehPass.outputEncoding = ""
        bokehPass.needsSwap = true;
        canvas.composer.addPass(bokehPass);
        const matChanger = () => {
            let bp = bokehPass as any;
            bp.uniforms['aperture'].value = effectController.aperture * 0.00001 * getCamDist();
            bp.uniforms['maxblur'].value = effectController.maxblur * this._blur;

        };
        
        matChanger();
        const gui = options.gui.addFolder("Bokeh");
        gui.add(effectController, 'aperture', 0, 50, 0.1).onChange(matChanger);
        gui.add(effectController, 'maxblur', 0.0, 0.05, 0.001).onChange(matChanger);

        let gammaCorrection = new ShaderPass(GammaCorrectionShader);
        canvas.composer.addPass(gammaCorrection);

        canvas.onUpdate.add(() => {
            let bp = bokehPass as any;

            let blur = effectController.maxblur * this._blur;
            bokehPass.enabled = blur > 0;
            gammaCorrection.enabled = bokehPass.enabled;

            if (!bokehPass.enabled) {
                return;
            }

            let p = this._target.clone();
            p = p.applyMatrix4(canvas.camera.matrixWorldInverse);
            let focus = Math.max(-p.z, 0);

            bp.uniforms['aperture'].value = effectController.aperture * 0.00001 * getCamDist();
            bp.uniforms['maxblur'].value = blur;
            bp.uniforms['focus'].value = focus;
        });



        // document.body.addEventListener("keyup",
        //     () => {
        //         gsap.to(this, { duration: 2, _blur: this._blur > 0 ? 0 : 1 });
        //     });
    }

    private _showTransform: boolean = false;
    public get showTransform(): boolean {
        return this._showTransform;
    }
    public set showTransform(value: boolean) {
        if (this._showTransform == value) {
            return;
        }
        this._showTransform = value;

        if (this._transform) {
            // this._transform.visible = value;
            // this._transformMesh.visible = value;

            if (value) {
                this._canvas.scene.add(this._transform);
            } else {
                // this._transform.detach();
                this._transform.removeFromParent();
            }
        }
    }


    private target(target: Vector3, duration: number = 0.5): void {
        gsap.to([
            this._target,
            this._transformMesh?.position
        ], {
            x: target.x,
            y: target.y,
            z: target.z,
            duration: duration,
            onComplete: () => {
                this.onChange.dispatch();
            }
        });
    }

    public show(t: VOSceneStorage, duration: number = 0.75): void {
        this.origin(
            new Vector3(
                t.camera_x,
                t.camera_y,
                t.camera_z
            ),
            duration);
        this.target(
            new Vector3(
                t.target_x,
                t.target_y,
                t.target_z
            ),
            duration);
    }

    private origin(target: Vector3, duration: number = 0.5): void {
        gsap.to([
            this._camera.position
        ], {
            x: target.x,
            y: target.y,
            z: target.z,
            duration: duration
        });
    }

    public getSceneStorage(id: number, kind: "marker" | "scene"): VOSceneStorage {
        return {
            id: id,
            kind: kind,
            camera_x: this._camera.position.x,
            camera_y: this._camera.position.y,
            camera_z: this._camera.position.z,
            target_x: this._target.x,
            target_y: this._target.y,
            target_z: this._target.z,
        };
    }
}